/* ─── IST (India, fixed +05:30, no DST) date-range helper ──────────
   Shared by the order lists so the preset math lives in one place.
   Presets operate on an order's `createdAt` and answer: does this
   timestamp fall inside the selected IST calendar range?            */

const IST_OFFSET_MIN = 330; // +05:30, fixed
const DAY_MS = 86400000;

/* Chip presets in display order. `key` is what components store in
   state; `label` is what the chip shows. "all" is the default. */
export const DATE_PRESETS = [
  { key: "all",       label: "All" },
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week",      label: "This week" },
  { key: "lastweek",  label: "Last week" },
  { key: "month",     label: "This month" },
];

/* IST calendar parts (year/month/day/weekday) for a given epoch ms.
   We shift into IST then read the UTC fields of the shifted clock. */
const istParts = (ms) => {
  const d = new Date(ms + IST_OFFSET_MIN * 60000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
    weekday: d.getUTCDay(), // 0=Sun … 6=Sat, in IST
  };
};

/* Epoch ms of 00:00:00 IST for the given IST calendar date. */
const istDayStart = (year, month, day) =>
  Date.UTC(year, month, day) - IST_OFFSET_MIN * 60000;

/* Returns true when `createdAt` falls inside `preset`'s IST range.
   Unknown / "all" / falsy preset ⇒ true (never regresses a list). */
export const dateInRange = (createdAt, preset) => {
  if (!preset || preset === "all") return true;
  if (!createdAt) return false;

  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;

  const nowMs = Date.now();
  const now = istParts(nowMs);
  const todayStart = istDayStart(now.year, now.month, now.day);
  const mondayOffset = (now.weekday + 6) % 7; // days since Monday (Mon=0)

  let start;
  let end;
  switch (preset) {
    case "today":
      start = todayStart;
      end = todayStart + DAY_MS;
      break;
    case "yesterday":
      start = todayStart - DAY_MS;
      end = todayStart;
      break;
    case "week": // Monday 00:00 IST … now
      start = todayStart - mondayOffset * DAY_MS;
      end = nowMs;
      break;
    case "lastweek": // previous Mon 00:00 … this Mon 00:00 (full Mon–Sun)
      start = todayStart - (mondayOffset + 7) * DAY_MS;
      end = todayStart - mondayOffset * DAY_MS;
      break;
    case "month": // 1st 00:00 IST … now
      start = istDayStart(now.year, now.month, 1);
      end = nowMs;
      break;
    default:
      return true;
  }

  return t >= start && t < end;
};
